<script setup lang="ts">
import { computed, ref } from 'vue'

import MediaImage from '@fondof-ui/components/media/MediaImage.vue'
import MediaRoot from '@fondof-ui/components/media/MediaRoot.vue'
import { contentfulUrl } from '@internal/format-contentful'
import Button from '@satch-ui/components/button/Button.vue'
import HeroContent from '@storefront-satch/components/molecules/hero/HeroContent.vue'
import HeroRoot from '@storefront-satch/components/molecules/hero/HeroRoot.vue'
import type { BrandHero } from '@storefront-satch/components/molecules/hero/types'
import MediaVideo from '@storefront-satch/components/molecules/media/MediaVideo.vue'
import { clsx } from 'clsx'

import { ArrowRightIcon, PlayIcon } from '@fond-of/satch-icons'

interface HeroFullProps extends BrandHero {
  /**
   * The Media type is used to differentiate between video and image to enable certain behaviours
   */
  mediaType?: 'image' | 'video' | 'additionalVideo' | null
}

const props = defineProps<HeroFullProps>()
const hasAdditionalImage = !!props.additionalImage
const isVideoPlaying = ref<boolean>(false)

const isLinkElement = computed(() => {
  return props.link && !isVideoWithoutAutoplay.value
})

const isVideoWithoutAutoplay = computed(() => {
  return props.mediaType === 'video' && !props.media?.videoAutoplay
})

const hasVideo = computed(() => {
  return props.mediaType === 'video' || props.mediaType === 'additionalVideo'
})
</script>
<template>
  <HeroRoot
    v-bind="props"
    data-label="Hero"
    :hero-id="props.sys?.id"
    :class="
      clsx('lg:min-h-none w-full max-sm:aspect-square', {
        'max:h-[calc(theme(screens[2xl])+0.281)] sm:h-[34vw] md:h-[28.1vw]':
          !hasAdditionalImage,
        'max:h-[calc(theme(screens[2xl])+0.281+theme(space.20))] sm:h-[calc(28.1vw+theme(space.10))] md:h-[calc(28.1vw+theme(space.15))] lg:h-[calc(28.1vw+theme(space.20))]':
          hasAdditionalImage,
      })
    "
    :has-additional-image="hasAdditionalImage"
  >
    <component
      :is="isLinkElement ? 'a' : 'div'"
      data-label="Hero Inner"
      :href="isLinkElement ? contentfulUrl(props.link) : undefined"
      :class="[
        'relative size-full',
        'flex flex-row flex-nowrap',
        {
          'items-center sm:justify-between': hasAdditionalImage,
          'items-end sm:items-center': !hasAdditionalImage,
        },
      ]"
      :data-gtm-attr="isLinkElement ? 'button_hero' : undefined"
      :data-gtm-label="isLinkElement ? props.internalTitle : undefined"
      :aria-label="
        isLinkElement
          ? props.linkAriaLabel?.trim() || props.linkText?.trim() || undefined
          : undefined
      "
    >
      <div
        v-if="props.media"
        data-label="Hero Image"
        :class="
          clsx('size-full self-start', {
            relative: !hasAdditionalImage,
            'max:h-[calc(theme(screens[2xl])+0.281)] absolute sm:right-0 sm:h-[28.1vw]':
              hasAdditionalImage,
          })
        "
      >
        <MediaRoot
          :entry="props.media"
          class="media absolute left-0 top-0 size-full object-cover object-top"
        >
          <template #video="{ data }">
            <MediaVideo
              v-bind="data"
              class="size-full"
              @update:video-playing="
                (state) => {
                  isVideoPlaying = state
                }
              "
            />
          </template>
          <template #image="{ data }">
            <MediaImage
              v-bind="data"
              class="size-full"
              :divider-mobile="2"
              :divider-tablet="1.5"
              :divider-desktop="1"
              lazyload="eager"
              fetchpriority="high"
            />
          </template>
        </MediaRoot>
      </div>
      <div
        data-label="content wrapper"
        :class="
          clsx('lg:pl-15 w-full pl-5 pr-5 sm:pl-10', {
            'absolute pb-5 sm:pb-0': !hasAdditionalImage,
            'relative max-w-[65%] sm:max-w-[55%]': hasAdditionalImage,
          })
        "
      >
        <div
          v-if="props.content"
          :class="[
            'select-none transition-opacity duration-100',
            {
              'opacity-1': mediaType === 'image',
              'cursor-pointer':
                mediaType === 'video' && !props.media?.videoAutoplay,
              'opacity-0':
                mediaType === 'video' &&
                isVideoPlaying === true &&
                !props.media?.videoAutoplay,
            },
          ]"
        >
          <HeroContent
            :content="props.content"
            :content-style="props.contentStyle"
            :has-additional-image="hasAdditionalImage"
            :headline-size="props.headlineSize"
            :headline-style="props.headlineStyle"
            :class-headline="{
              'text-3xl sm:text-2xl md:text-3xl':
                !hasAdditionalImage && props.headlineSize !== 'super',
              'text-xl sm:text-lg md:text-2xl':
                hasAdditionalImage && props.headlineSize !== 'super',
              'text-4xl': props.headlineSize === 'super',
            }"
            :class-subline="[
              'md:w-[60%]',
              {
                'mb-2': !props.linkStyle || !props.linkText,
              },
            ]"
          />
          <div
            v-if="props.linkStyle && props.linkText"
            class="mt-4 w-full"
          >
            <Button
              v-if="props.linkText && (props.link || isVideoWithoutAutoplay)"
              class="font-sans-medium font-semibold"
              :variant="props.linkStyle"
              padding="large"
              text-size="large"
            >
              {{ props.linkText }}
              <PlayIcon
                v-if="isVideoWithoutAutoplay && hasVideo"
                class="w-4"
              />
              <ArrowRightIcon
                v-else
                class="w-4"
              />
            </Button>
          </div>
        </div>
      </div>
      <div
        v-if="hasAdditionalImage"
        :class="
          clsx(
            'absolute right-0 z-[1] aspect-square h-[62%] w-[35%] sm:top-10 sm:h-[calc(100%-40px)] sm:w-auto',
            'sm:right-[60px] md:right-[80px] lg:right-[100px]'
          )
        "
      >
        <MediaRoot
          :entry="props.additionalImage"
          class="relative size-full"
        >
          <template #video="{ data }">
            <MediaVideo
              v-bind="data"
              class="size-full"
              @update:video-playing="
                (state) => {
                  isVideoPlaying = state
                }
              "
            />
          </template>
          <template #image="{ data }">
            <MediaImage
              v-bind="data"
              cover-image
              :divider-mobile="2"
              :divider-tablet="2"
              :divider-desktop="1.5"
              lazyload="eager"
              fetchpriority="high"
            />
          </template>
        </MediaRoot>
      </div>
    </component>
  </HeroRoot>
</template>
